import {useLocation, useNavigate} from "react-router-dom";
import {CloseButton, Container, Group, Loader, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  CustomFieldProfile, CustomFieldProfileItem,
  customFieldProfileSelector,
  getObjectId,
  useGetCustomFieldProfileQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  CustomFieldProfileForm,
  CustomFieldProfileFormDefaultValues,
  CustomFieldProfileFormValues
} from "../../components/form/CustomFieldProfileForm/CustomFieldProfileForm";
import {useCustomFieldProfileMutation} from "../../hooks/useCustomFieldProfileMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {
  toFormProfileDefaultsFromCustomFieldProfile
} from "../../components/form/CustomFieldProfileForm/useCustomFieldProfileForm";
import { useApp } from "../../providers/RegionProvider/RegionProvider";
import {useCustomFieldProfileItemMutation} from "../../hooks/useCustomFieldProfileItemMutation";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: CustomFieldProfileFormDefaultValues
}

export const CustomFieldProfileFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <CustomFieldProfileFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const CustomFieldProfileFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const {regionId} = useApp();

  const [access, setAccess] = useState(false);

  const selectCustomFieldProfile = useMemo(() => {
    return customFieldProfileSelector()
  }, [])

  const {
    currCustomFieldProfile,
    isLoading
  } = useGetCustomFieldProfileQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currCustomFieldProfile: selectCustomFieldProfile(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: CustomFieldProfileFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormProfileDefaultsFromCustomFieldProfile(currCustomFieldProfile);
  }, [currCustomFieldProfile, formDefaults]);

  const isEdit = useMemo(() => {
    return currCustomFieldProfile && !copy
  }, [currCustomFieldProfile, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("common.page.entity.customfieldprofile.form.title.edit", "Edit Custom Field Profile", {ns: "common"});
    } else {
      return t("common.page.entity.customfieldprofile.form.title.new", "New Custom Field Profile", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createCustomFieldProfile,
    updateCustomFieldProfile,
    isCreating,
    isUpdating,
  } = useCustomFieldProfileMutation()

  const {
    patchCustomFieldProfileItemsByProfile,
  } = useCustomFieldProfileItemMutation()

  const onCustomFieldProfileSubmit = useCallback((formValues: CustomFieldProfileFormValues) => {
    const patch: Partial<CustomFieldProfile> = {
      companyId: getObjectId(formValues.company),
      forObjectType: formValues.objectType,
      name: formValues.name,
      description: formValues.description,
      effectiveFromDttm: adjustToTimeZone(formValues.effectiveFromDate).getTime(),
      effectiveToDttm: adjustToTimeZoneSafely(formValues.effectiveToDate)?.getTime(),
    }

    const items : Partial<CustomFieldProfileItem>[] = []
    if (formValues.catalog) {
      for (const item of formValues.catalog) {
        let id: string | undefined = undefined
        if (item.profileItemId) {
          if (!item.profileItemId.startsWith("temp")) {
            id = item.profileItemId
          }
        }

        items.push({
          id: id,
          customFieldProfileId: currCustomFieldProfile?.id,
          customFieldDefId: item.defId,
          sortOrder: item.profileItemSortOrder,
          required: item.profileItemRequired,
          label: item.profileItemLabel,
        })
      }
    }

    if (isEdit) {
      const profile: Partial<CustomFieldProfile> = {
        ...currCustomFieldProfile.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateCustomFieldProfile(profile, {
        onSuccess: (payload) => {
          patchCustomFieldProfileItemsByProfile(payload.id, items, {
            onSuccess: () => navigate(-1),
          })
        }
      })
    } else {
      const profile: Partial<CustomFieldProfile> = {
        ...patch,
      };
      createCustomFieldProfile(profile, {
        onSuccess: (payload) => {
          patchCustomFieldProfileItemsByProfile(payload.id, items, {
            onSuccess: () => navigate(-1),
          })
        }
      })
    }
  }, [isEdit, currCustomFieldProfile, updateCustomFieldProfile, navigate, createCustomFieldProfile, patchCustomFieldProfileItemsByProfile]);

  return (
    <ProtectedPage
      entity={currCustomFieldProfile}
      permissions={[isEdit ? "customfieldprofile_edit" : "customfieldprofile_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          {isLoading && <Loader/>}
          {!isLoading && (
            <>
              <Group position="apart">
                <Title align="center">
                  {title}
                </Title>
                <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
              </Group>
              <Space h="md"/>
              <CustomFieldProfileForm
                options={{
                  regionId: regionId,
                  customFieldProfileId: currCustomFieldProfile?.id,
                }}
                defaultValues={_formDefaults}
                submitting={isCreating || isUpdating}
                onSubmit={onCustomFieldProfileSubmit}/>
            </>
          )}
        </Container>
      )}
    </ProtectedPage>
  );
};

